import { type ReactNode, useEffect, useRef } from "react";

/**
 * The table shell every catalogue list shares.
 *
 * WHAT THIS EXISTS FOR, and it is not DRY. The products list deferred this
 * deliberately -- "a table designed against one imagined consumer tends to fit
 * the second one badly" -- until a second list made the real requirements
 * visible. The brands list is that second consumer, and what the two actually
 * share turns out not to be columns at all. It is the ACCESSIBILITY scaffolding:
 *
 *   - `scope="col"` on every header, without which a screen reader cannot say
 *     which column a cell belongs to -- and these cells (a bare SKU, a bare
 *     segment) are close to meaningless unlabelled
 *   - `tabIndex` and a keyboard handler on each row, because a bare `onClick`
 *     on a `<tr>` is unreachable without a mouse
 *   - a per-row `aria-label`, so the target is named rather than announced as
 *     six unrelated cells
 *   - `aria-busy` and the dimming during a background refetch
 *
 * Every one of those is a detail that survives being written once and gets
 * silently dropped the third time a table is copy-pasted. That is the argument
 * for extracting: not repetition, but that the repeated part is the part most
 * easily lost.
 *
 * Columns stay per-screen. They are the half that genuinely differs, and a
 * column DSL rich enough to express both lists would be harder to read than the
 * JSX it replaced.
 */

export interface DataTableColumn<Row> {
	/** Header text. Also what a screen reader reads for the column. */
	header: string;
	/** The cell. Given the row, returns whatever should render inside `<td>`. */
	cell: (row: Row) => ReactNode;
	/** Right-align, for numbers. */
	align?: "left" | "right";
	/** Extra classes on the `<td>` -- `font-mono`, `tabular-nums`, `capitalize`. */
	cellClassName?: string;
}

export interface DataTableProps<Row> {
	rows: Row[];
	columns: DataTableColumn<Row>[];
	/** Stable identity. Index would reorder wrongly on a filter change. */
	rowKey: (row: Row) => string;
	/**
	 * What activating a row does. Omit for a table that is only read: the row
	 * then gets no pointer affordance, no tab stop and no label, because
	 * offering all three for something that does nothing is a promise the table
	 * cannot keep -- the products list removed exactly that highlight while its
	 * editor did not yet exist.
	 */
	onRowActivate?: (row: Row) => void;
	/** What activating this row does, for a screen reader. e.g. `Edit Kajaria`. */
	rowLabel?: (row: Row) => string;
	/** A background refetch is in flight: dim, and say so. */
	isRefreshing?: boolean;
	/**
	 * Row selection, for the bulk actions in plan 008 section 1.
	 *
	 * It lives HERE rather than as a caller-supplied first column because of the
	 * HEADER: a select-all control cannot be expressed through `column.header`,
	 * which is a string.
	 *
	 * The row checkbox is a weaker argument and this docblock used to overstate
	 * it. A caller COULD render one from `column.cell`, which returns a
	 * ReactNode, and stop the click itself -- which is exactly what the `<td>`
	 * below does. Keeping the two together is a choice about coherence, not an
	 * impossibility: a select-all that lived here while the row boxes lived in
	 * every caller would drift the first time one of them forgot the
	 * stopPropagation.
	 *
	 * Optional, so a table that only reads -- the brands list -- is unchanged.
	 */
	selection?: {
		selected: ReadonlySet<string>;
		onToggle: (id: string) => void;
		/** Select every row on this page, or clear them if all are selected. */
		onToggleAll: () => void;
		/** What the row's checkbox says to a screen reader, e.g. `Select Kajaria`. */
		label: (row: Row) => string;
		/**
		 * What the HEADER checkbox says, when "this page" is not the whole story.
		 *
		 * The products list has a mode where the selection is a filter rather than
		 * a list of rows, and there the default wording is false in both states:
		 * "Clear selection" would clear the page and leave 340 products selected,
		 * and "Select every product on this page" describes stopping an exclusion.
		 * A table that has no such mode passes nothing and keeps the old wording.
		 */
		allLabel?: (allSelected: boolean) => string;
		/**
		 * True when SOME but not all of the wider selection is on this page --
		 * matching mode with exclusions. Renders the checkbox indeterminate, which
		 * is the only one of the three states a boolean `checked` cannot express.
		 */
		partial?: boolean;
	};
}

export function DataTable<Row>({
	rows,
	columns,
	rowKey,
	onRowActivate,
	rowLabel,
	isRefreshing,
	selection,
}: DataTableProps<Row>) {
	const interactive = Boolean(onRowActivate);
	const allSelected =
		rows.length > 0 &&
		rows.every((row) => selection?.selected.has(rowKey(row)));
	const headerRef = useRef<HTMLInputElement>(null);
	/*
	 * `indeterminate` is a PROPERTY, not an attribute, so React cannot set it
	 * from JSX -- it has to be written to the node. Without it a header with
	 * three states renders as two, and "some of this page is excluded" looks
	 * identical to "none of it is selected".
	 */
	useEffect(() => {
		if (headerRef.current) {
			headerRef.current.indeterminate = Boolean(selection?.partial);
		}
	}, [selection?.partial]);

	return (
		<div
			className={`overflow-x-auto transition-opacity${
				isRefreshing ? " opacity-50" : ""
			}`}
			aria-busy={isRefreshing || undefined}
		>
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
						{selection ? (
							<th scope="col" className="w-8 py-2 pr-3 font-medium">
								<input
									ref={headerRef}
									type="checkbox"
									className="size-4 cursor-pointer align-middle"
									checked={allSelected}
									onChange={selection.onToggleAll}
									aria-label={
										selection.allLabel
											? selection.allLabel(allSelected)
											: allSelected
												? "Clear selection"
												: "Select every product on this page"
									}
								/>
							</th>
						) : null}
						{columns.map((column) => (
							<th
								key={column.header}
								scope="col"
								className={`py-2 pr-3 font-medium${
									column.align === "right" ? " text-right" : ""
								}`}
							>
								{column.header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr
							key={rowKey(row)}
							tabIndex={interactive ? 0 : undefined}
							aria-label={interactive ? rowLabel?.(row) : undefined}
							className={
								interactive
									? "cursor-pointer border-b last:border-0 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
									: "border-b last:border-0"
							}
							onClick={interactive ? () => onRowActivate?.(row) : undefined}
							onKeyDown={
								interactive
									? (event) => {
											// Enter AND Space: a row behaving as a button has to
											// answer to both, which is the pair a native <button>
											// would have given for free.
											if (event.key === "Enter" || event.key === " ") {
												event.preventDefault();
												onRowActivate?.(row);
											}
										}
									: undefined
							}
						>
							{selection ? (
								// `stopPropagation`, because the row's own click handler would
								// otherwise open the editor every time somebody ticked a box --
								// the event reaches it on the way up whatever this cell does.
								<td
									className="w-8 py-2 pr-3"
									onClick={(event) => event.stopPropagation()}
									onKeyDown={(event) => event.stopPropagation()}
								>
									<input
										type="checkbox"
										className="size-4 cursor-pointer align-middle"
										checked={selection.selected.has(rowKey(row))}
										onChange={() => selection.onToggle(rowKey(row))}
										aria-label={selection.label(row)}
									/>
								</td>
							) : null}
							{columns.map((column) => (
								<td
									key={column.header}
									className={`py-2 pr-3${
										column.align === "right" ? " text-right" : ""
									}${column.cellClassName ? ` ${column.cellClassName}` : ""}`}
								>
									{column.cell(row)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
